import {
  AwardIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PlusIcon,
  RupeeIcon,
  TextInput,
} from '@razorpay/blade/components';
import useFetchPayDetailsData from 'components/PayDetails/hooks/useFetchPayDetailsData';
import useSalaryComponentsList from 'components/RunPayroll/Popups/EditSalary/hooks/useSalaryComponentsList';
import { DeductionsComponentType } from 'components/SalaryComponents/types';
import AutoCompleteV2 from 'components/ui/AutoCompleteV2/AutoCompleteV2';
import { ValidationSchema, useForm } from 'forms';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { RecurringDeductionsFormData } from '../types';
import { getTaxabilityDescriptionText } from '../utils';
import { useSalaryComponentHelpers } from 'components/SalaryComponents/hooks/useSalaryComponentHelpers';

// Validation schema for recurring deductions form
const validationSchema: ValidationSchema<RecurringDeductionsFormData> = {
  componentId: (formData) => {
    if (!formData.componentId) return 'Component is required';
    return null;
  },
  monthlyAmount: (formData) => {
    if (formData.monthlyAmount === undefined || formData.monthlyAmount === null) return 'Monthly amount is required';
    if (formData.monthlyAmount === 0) return 'Monthly amount cannot be 0';
    return null;
  },
};

const RecurringDeductionsModal = ({
  initialFormData,
  isOpen,
  isEdit,
  existingDeductionsIdList = [],
  onDismiss,
  onSubmit,
}: {
  initialFormData: RecurringDeductionsFormData;
  isOpen: boolean;
  onDismiss: () => void;
  onSubmit: (formData: RecurringDeductionsFormData) => void;
  existingDeductionsIdList?: string[];
  isEdit?: boolean;
}) => {
  const { formData, setFieldValue, register, validate } =
    useForm<RecurringDeductionsFormData>(initialFormData);
  const { urlParams } = useSearchParams();
  const userId = urlParams.get('userId');
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const peopleId = String(userId ?? loggedInUserId);
  const { firstName } = useFetchPayDetailsData(peopleId);

  const { searchSalaryComponents, isFetchingSalaryComponents } =
    useSalaryComponentsList<DeductionsComponentType>({
      requestParams: {
        category: 'DEDUCTIONS_CATEGORY',
        payType: 'LIST_RECURRING_TYPE',
        status: 'COMPONENT_STATUS_ACTIVE',
      },
    });

  const isFormValid = useMemo(
    () => validate(validationSchema, { sideEffects: false }),
    [formData, validate],
  );

  const onChange = (value: string) => {
    setFieldValue('componentId', value);
  };

  const onUpdateDeductions = () => {
    // If validation passes, formData is guaranteed to have the required fields
    onSubmit(formData as RecurringDeductionsFormData);
  };

  const { openDeductionsCreation } = useSalaryComponentHelpers();

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} size="medium">
      <ModalHeader
        title={`${isEdit ? 'Modify' : 'Add'} Recurring Deduction for ${firstName}`}
        subtitle={'Monthly deductions which are not part of CTC'}
        leading={
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            backgroundColor={'surface.background.primary.subtle'}
            padding={'spacing.3'}
            borderRadius={'round'}
            marginTop={'spacing.4'}>
            <AwardIcon color="surface.icon.primary.normal" />
          </Box>
        }
      />
      <ModalBody padding="spacing.6">
        <Box
          padding={'spacing.6'}
          display="flex"
          flexDirection="column"
          gap="spacing.5"
          backgroundColor={'surface.background.gray.moderate'}
          borderRadius={'large'}>
          <AutoCompleteV2
            label="Recurring Deduction"
            placeholder="Search for a component"
            options={searchSalaryComponents(existingDeductionsIdList)}
            onChange={(item) => onChange(item.id)}
            value={formData.componentId ?? ''}
            itemToLabel={(item) =>
              item.settings.behaviour.displayBehaviour?.displayName ?? item.name
            }
            itemToKey={(item) => item.id}
            itemToDescription={(item) =>
              getTaxabilityDescriptionText(item.settings.behaviour.taxBehaviour)
            }
            footer={
              <Button
                variant="tertiary"
                size="small"
                icon={PlusIcon}
                onClick={openDeductionsCreation}
                isFullWidth>
                Create deduction
              </Button>
            }
            necessityIndicator={'required'}
            width={'680px'}
            isDisabled={isFetchingSalaryComponents}
            data-testid="deduction-autocomplete"
          />
          <TextInput
            label="Monthly Amount"
            necessityIndicator={'required'}
            placeholder="Enter amount"
            leadingIcon={RupeeIcon}
            value={formData.monthlyAmount ? formData.monthlyAmount.toString() : ''}
            onChange={(e) => {
              const value = e.value || '';
              setFieldValue('monthlyAmount', value === '' ? 0 : parseFloat(value));
            }}
            {...register('monthlyAmount')}
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.5">
          <Button
            variant="tertiary"
            onClick={onDismiss}
            isLoading={isFetchingSalaryComponents}
            data-testid="deduction-back-button">
            Back
          </Button>
          <Button
            onClick={onUpdateDeductions}
            isLoading={isFetchingSalaryComponents}
            isDisabled={!isFormValid}
            data-testid="deduction-submit-button">
            {isEdit ? 'Confirm Modification' : 'Add Deduction'}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default RecurringDeductionsModal;
